import { useEffect } from 'react';
import styled from 'styled-components';
import { useCoreAuth } from '@carvana/core-auth';
import { media } from '@carvana/stc-styles';
import { hasMobileUserAgent } from '@carvana/stc-utilities';

import useAccountModal from '../../hooks/useAccountModal';
import { CtaButtonShort, CtaButtonShortOutline } from '../../theme/layoutstyles';
import { HeaderTwo } from '../../theme/typographystyles';
import { requestCredentialsUpdate } from '../../utilities/mobileCredentials';
import { recordAction, recordActionImmediately } from '../../utilities/moMetrics/uiMetrics';

const ErrorWrapper = styled.div`
  align-content: middle;
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-self: center;
  padding-bottom: 50px;
  padding-top: 50px;
  text-align: center;
`;

const ErrorDescription = styled.div`
  color: ${({ theme }) => theme.carvana.gray.dark};
  font-size: 18px;
  text-align: center;
`;

const Ctas = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  margin-top: 32px;
`;

const Search = styled(CtaButtonShortOutline)`
  && {
    margin-left: 8px;

    ${media.sm`
      margin-left: 16px;
    `}
  }
`;

type ErrorPageProps = {
  error?: string;
  header: string;
  subheader: string;
};

const ErrorPage = ({ error = '', header, subheader }: ErrorPageProps) => {
  const { signIn } = useAccountModal('Shell', 'ErrorPage');
  const { getUserState } = useCoreAuth();
  const { isLoggedIn } = getUserState();

  useEffect(() => {
    recordAction('ErrorPage', 'Landing', 'Rendered');
  }, []);

  const getCtaButtonShort = errorType => {
    if (errorType === '404' || isLoggedIn) {
      return (
        <CtaButtonShort
          as="a"
          onClick={async () => {
            await recordActionImmediately('ErrorPage', 'Sell Your Car', 'Clicked');
            window.location.href = '/sell-my-car';
          }}
        >
          Sell your car
        </CtaButtonShort>
      );
    }

    const signInAction = async () => {
      await recordActionImmediately('ErrorPage', 'Sign In', 'Clicked');
      if (hasMobileUserAgent()) {
        await requestCredentialsUpdate();
      } else {
        signIn(() => window.location.reload());
      }
    };

    return <CtaButtonShort onClick={signInAction}>Sign in</CtaButtonShort>;
  };

  return (
    <ErrorWrapper>
      <HeaderTwo>{header}</HeaderTwo>
      <ErrorDescription>{subheader}</ErrorDescription>
      <Ctas>
        {getCtaButtonShort(error)}
        <Search
          as="a"
          onClick={async () => {
            await recordActionImmediately('ErrorPage', 'Find A Car', 'Clicked');
            window.location.href = '/cars';
          }}
        >
          Find a car
        </Search>
      </Ctas>
    </ErrorWrapper>
  );
};

export default ErrorPage;
